import { useState, useEffect, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { debounce } from 'lodash';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
	Button,
	Caption,
	Grid,
	H3,
	InputField,
	Loader,
	SmallText,
	Text,
	TypeAhead,
} from '@components';
import {
	BUTTON_TYPES,
	BUTTON_SIZE,
	STATIC_ADMIN_SHIPMENT_STATUS,
	TTransportShipment,
	TypeAheadOption,
	ShipperSearch,
	CarrierResult,
} from '@types';
import { CarrierModuleOpen } from '@state';
import { useEOSFetchShipments, useGetShipper } from '@api';
// import { useLocalStorage } from '@hooks';
import {
	runCarrierRec, runEfficiencyRec, getRef,
	todayMidnight, addDays,
	downloadCarrierCSV,
} from '@utils';
import { ModulePanel } from '@components';
import { CarrierCard } from './CarrierCard';
// import { SaveShipperInput } from './SaveShipperInput';
import styles from './ModuleCarrierRecommendation.module.scss';

const EOS_FIELDS = [
	'id', 'reference',
	'original_pickup_on', 'original_dropoff_on',
	'cargos', 'req_truck_type', 'req_truck_size',
	'temperature', 'freight_mode', 'cargo_type',
	'manifest.master_manifest.carrier.company.business_name',
	'manifest.master_manifest.carrier.id',
	'master_shipment.shipper.company.business_name',
	'master_shipment.shipper.cost_centre_name',
	'job_list_id',
	'revised_pickup.request_arrival_date',
	'revised_pickup.request_arrival_start_time',
	'revised_pickup.request_arrival_end_time',
	'revised_dropoff.request_arrival_date',
	'revised_dropoff.request_arrival_start_time',
	'revised_dropoff.request_arrival_end_time',
	'superdock_carriers'
];

export const ModuleCarrierRecommendation = () => {
	const [isOpen, setIsOpen] = useRecoilState(CarrierModuleOpen);
	const [isRunning, setIsRunning] = useState(false);
	const [results, setResults] = useState<CarrierResult[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [refSearch, setRefSearch] = useState('');
	const [shipperSearch, setShipperSearch] = useState('');
	const [shipperOpts, setShipperOpts] = useState<TypeAheadOption[]>([]);
	// const [savedShippers, setSavedShippers] = useLocalStorage<string[]>('__eosShipperPrefs', []);
	const [activeShipperFilters] = useState<Set<string>>(new Set());
	const [efficiencyRefs, setEfficiencyRefs] = useState<Set<string>>(new Set());

	const { fetchShipments } = useEOSFetchShipments();
	const { loadingShippers, shippers, callGetShippers } = useGetShipper();

	const searchShippers = useCallback(debounce((val: string) => {
		callGetShippers('shipper', val);
	}, 500), []);

	const handleRun = async () => {
		setIsRunning(true);
		setError(null);
		try {
			const today = todayMidnight();
			const cutoff = addDays(today, 7);
			const toDateInt = (d: Date) =>
				d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();

			const fetchAllPages = async (basePayload: object): Promise<TTransportShipment[]> => {
				const firstRes = await fetchShipments({ avoidCache: true, payload: { ...basePayload, page: '1' } });
				const firstHits = (firstRes?.data?.hits || []).map((h) => h.document as TTransportShipment);
				const found = firstRes?.data?.found ?? 0;
				const totalPages = Math.ceil(found / 100);
				if (totalPages <= 1) return firstHits;
				const remainingRes = await Promise.all(
					Array.from({ length: totalPages - 1 }, (_, i) =>
						fetchShipments({ avoidCache: true, payload: { ...basePayload, page: String(i + 2) } })
					)
				);
				return [
					...firstHits,
					...remainingRes.flatMap((res) => (res?.data?.hits || []).map((h) => h.document as TTransportShipment)),
				];
			};

			const [unallocated, allocated] = await Promise.all([
				fetchAllPages({
					fields: EOS_FIELDS, q: '*', per_page: '100',
					filter_by: {
						id: '',
						type: 'and',
						args: {
							operands: [
								{ type: '=', args: { field: 'shipment_status.for_admin', value: STATIC_ADMIN_SHIPMENT_STATUS.PENDING } },
								{ type: '>=', args: { field: 'original_pickup_on', value: toDateInt(today) } },
								{ type: '<=', args: { field: 'original_pickup_on', value: toDateInt(cutoff) } },
							]
						}
					},
					sort_by: [{ field: 'original_pickup_on', direction: 'asc' }],
				}),
				fetchAllPages({
					fields: EOS_FIELDS, q: '*', per_page: '100',
					filter_by: {
						id: '',
						type: 'and',
						args: {
							operands: [
								{ type: '!=', args: { field: 'shipment_status.for_admin', value: STATIC_ADMIN_SHIPMENT_STATUS.PENDING } },
								{ type: '!=', args: { field: 'shipment_status.for_admin', value: STATIC_ADMIN_SHIPMENT_STATUS.CANCELLED } },
								{ type: '>=', args: { field: 'original_dropoff_on', value: toDateInt(addDays(today, -1)) } },
								{ type: '<=', args: { field: 'original_dropoff_on', value: toDateInt(cutoff) } },
							]
						}
					},
					sort_by: [{ field: 'original_dropoff_on', direction: 'desc' }],
				}),
			]);

			const efficiencyOpps = runEfficiencyRec(unallocated);
			setEfficiencyRefs(new Set(efficiencyOpps.flatMap((opp) => opp.group.map((s) => getRef(s)))));
			setResults(runCarrierRec(unallocated, allocated));
		} catch (e) {
			setError(e instanceof Error ? e.message : 'An error occurred');
		} finally {
			setIsRunning(false);
		}
	};

	const filteredResults = (results || []).filter((r) => {
		const refTerms = refSearch.toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
		const refMatch = !refTerms.length || refTerms.some((t) => r.ref.toLowerCase().includes(t));
		const shipperMatch = !shipperSearch || r.shipperSearchIndex.includes(shipperSearch.toLowerCase());
		const chipMatch = activeShipperFilters.size === 0 ||
			[...activeShipperFilters].some((s) => r.shipperSearchIndex.includes(s.toLowerCase()));
		return r.matches.length > 0 && refMatch && shipperMatch && chipMatch;
	});
	const withoutMatches = (results || []).filter((r) => r.matches.length === 0);

	useEffect(() => {
		const results = shippers?.searchResult?.shipper ?? [];
		setShipperOpts(results.map((s: ShipperSearch) => ({
			label: (
				<div>
					<Text>{s.trade_name ?? ''}</Text>
					<Caption color='var(--color-grey-500)'>{s.company_name}</Caption>
				</div>
			),
			value: s,
		})));
	}, [shippers]);

	if (!isOpen) return null;

	return (
		<ModulePanel
			title='Carrier Recommendation'
			Icon={LocalShippingOutlinedIcon}
			onClose={() => setIsOpen(false)}
			onRun={handleRun}
			isRunning={isRunning}
			moduleId='carrier'
		>
			{error && <Text text={error} color='var(--color-error)' />}

			{!results && !isRunning && (
				<Text>
					Finds carriers already delivering near your unallocated pickups — today through the next 7 days.
				</Text>
			)}

			{isRunning && (
				<div className={styles.loading}>
					<Loader size={18} />
					<Text>Loading shipment data…</Text>
				</div>
			)}

			{results && !isRunning && (
				<>
					<div className={styles.stats}>
						<div className={styles.stat}>
							<H3>{results.filter((r) => r.matches.length > 0).length}</H3>
							<Caption>With carrier matches</Caption>
						</div>
						<div className={styles.stat}>
							<H3>{withoutMatches.length}</H3>
							<Caption>No match found</Caption>
						</div>
					</div>

					<div className={styles.filterArea}>
						<Grid columns={2} minWidth='300px' rowGap='8px'>
							<InputField
								name='refSearch'
								value={refSearch}
								onChange={(e) => setRefSearch(e.target.value)}
								placeholder='Search shipment refs… separate with commas'
								onClearInput={() => setRefSearch('')}
								stretch
								keepErrorSpace={false}
							/>
							<TypeAhead
								name='shipperSearch'
								value={shipperSearch}
								onChange={(e) => {
									const val = (e as React.ChangeEvent<HTMLInputElement>).target.value;
									setShipperSearch(val);
									if (val) searchShippers(val);
									else setShipperOpts([]);
								}}
								onSelected={(opt) => {
									const shipper = (opt as TypeAheadOption).value as ShipperSearch;
									setShipperSearch(shipper.trade_name ?? '');
									setShipperOpts([]);
								}}
								onClearInput={() => {
									setShipperSearch('');
									setShipperOpts([]);
								}}
								options={shipperOpts}
								placeholder='Filter by shipper…'
								isLoadingOptions={loadingShippers}
								stretch
								keepErrorSpace={false}
							/>
						</Grid>

						{/* WILL COME BACK TO THIS --> */}
						{/* <div className={styles.chipRow}>
							<Caption>Saved shippers:</Caption>
							{savedShippers.length === 0 && <Caption color='var(--color-grey-400)'>None saved yet</Caption>}
							{savedShippers.map((s, i) => (
								<span
									key={i}
									className={`${styles.chip} ${activeShipperFilters.has(s) ? styles.chipActive : ''}`}
									onClick={() => {
										setActiveShipperFilters((prev) => {
											const next = new Set(prev);
											next.has(s) ? next.delete(s) : next.add(s);
											return next;
										});
									}}
								>
									{s}
									<span
										className={styles.chipRemove}
										onClick={(e) => {
											e.stopPropagation();
											setSavedShippers((prev) => prev.filter((_, j) => j !== i));
											setActiveShipperFilters((prev) => { const n = new Set(prev); n.delete(s); return n; });
										}}
									>×</span>
								</span>
							))}
							<SaveShipperInput onSave={(name) => setSavedShippers((prev) => prev.includes(name) ? prev : [...prev, name])} />
						</div> */}
					</div>

					<div className={styles.statBar}>
						<SmallText>
							{filteredResults.length} shipment{filteredResults.length !== 1 ? 's' : ''} with suggestions
						</SmallText>
						<Button
							text='CSV'
							onClick={() => downloadCarrierCSV(results, efficiencyRefs)}
							type={BUTTON_TYPES.TERTIARY}
							size={BUTTON_SIZE.SMALL}
							Icon={FileDownloadIcon}
						/>
					</div>

					{filteredResults.length === 0 && results.length > 0 && (
						<Text>No shipments match your search.</Text>
					)}

					{filteredResults.map((r) => (
						<CarrierCard key={r.ref} result={r} efficiencyRefs={efficiencyRefs} />
					))}
				</>
			)}
		</ModulePanel>
	);
};
